import type { FileId } from "@excalidraw/element/types";

import type { BinaryFileData } from "@excalidraw/excalidraw/types";

import { FileManager } from "../data/FileManager";

import { api } from "./apiClient";

/**
 * Upload a Blob/string to a pre-signed S3 PUT URL. When `tagging` is set the
 * matching `x-amz-tagging` header must be sent (the presigned URL was signed
 * with that Tagging param) — used to tag autosave objects for the lifecycle
 * backstop.
 */
export const putToPresigned = async (
  url: string,
  body: Blob | string,
  contentType: string,
  tagging?: string,
): Promise<void> => {
  const headers: Record<string, string> = { "Content-Type": contentType };
  if (tagging) {
    headers["x-amz-tagging"] = tagging;
  }
  const res = await fetch(url, { method: "PUT", headers, body });
  if (!res.ok) {
    throw new Error(`upload failed (${res.status})`);
  }
};

export const getTextFromPresigned = async (url: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`download failed (${res.status})`);
  }
  return res.text();
};

/**
 * Files are stored content-addressed by fileId. We persist the Excalidraw
 * `dataURL` as the object body (matches how the editor consumes files).
 */
const uploadFile = async (
  url: string,
  file: BinaryFileData,
  tagging?: string,
): Promise<void> => {
  await putToPresigned(url, file.dataURL, "application/octet-stream", tagging);
};

/**
 * Build a FileManager that reads/writes binary files via the cloud API's
 * pre-signed URLs. `getUploadUrls`/`tagging` are supplied per save by the
 * cloud service (the API returns presigned PUT URLs only for new fileIds).
 */
export const createS3FileManager = (opts: {
  // Resolve presigned PUT urls for the given (new) fileIds.
  resolveUploadUrls: (fileIds: FileId[]) => Promise<Record<string, string>>;
  // Resolve presigned GET urls for the given fileIds.
  resolveDownloadUrls: (fileIds: FileId[]) => Promise<Record<string, string>>;
  tagging?: () => string | undefined;
}) => {
  return new FileManager({
    saveFiles: async ({ addedFiles }) => {
      const savedFiles = new Map<FileId, BinaryFileData>();
      const erroredFiles = new Map<FileId, BinaryFileData>();

      const ids = [...addedFiles.keys()];
      let urls: Record<string, string> = {};
      try {
        urls = await opts.resolveUploadUrls(ids);
      } catch {
        for (const [id, file] of addedFiles) {
          erroredFiles.set(id, file);
        }
        return { savedFiles, erroredFiles };
      }

      const tag = opts.tagging?.();
      await Promise.all(
        [...addedFiles].map(async ([id, file]) => {
          const url = urls[id as string];
          if (!url) {
            // No upload URL means the file already exists server-side (dedup).
            savedFiles.set(id, file);
            return;
          }
          try {
            await uploadFile(url, file, tag);
            savedFiles.set(id, file);
          } catch {
            erroredFiles.set(id, file);
          }
        }),
      );

      return { savedFiles, erroredFiles };
    },

    getFiles: async (ids) => {
      const loadedFiles: BinaryFileData[] = [];
      const erroredFiles = new Map<FileId, true>();

      let urls: Record<string, string> = {};
      try {
        urls = await opts.resolveDownloadUrls(ids);
      } catch {
        ids.forEach((id) => erroredFiles.set(id, true));
        return { loadedFiles, erroredFiles };
      }

      await Promise.all(
        ids.map(async (id) => {
          const url = urls[id as string];
          if (!url) {
            erroredFiles.set(id, true);
            return;
          }
          try {
            const dataURL = await getTextFromPresigned(url);
            loadedFiles.push({
              id,
              dataURL: dataURL as BinaryFileData["dataURL"],
              mimeType: "image/png" as BinaryFileData["mimeType"],
              created: Date.now(),
            });
          } catch {
            erroredFiles.set(id, true);
          }
        }),
      );

      return { loadedFiles, erroredFiles };
    },
  });
};

// Re-export for callers that just need the raw API.
export { api };
